import { ApplicationName, AppState } from "@/lib/types";
import useDockStore from "@/store/dockStore";
import useMethodStore, { useMethodActions } from "@/store/MethodStore";
import useSettingsStore from "@/store/SettingsStore";
import { ReactNode, useState } from "react";
import Draggable from "react-draggable";
import { IoMdClose, IoMdContract, IoMdExpand, IoMdRemove } from "react-icons/io";

export default function DraggableWindow({ children, applicationName, trigger, hasFullScreen = false, isOpen, setIsOpen, enableDragOn }: {
  children: ReactNode,
  applicationName: ApplicationName,
  trigger: ReactNode,
  hasFullScreen?: boolean
  isOpen: AppState,
  enableDragOn?: "lg" | "md" | "always"
  setIsOpen: (value: AppState) => void
}) {
  const [maximizeEffect, setMaximizeEffect] = useState(false)

  const { setFocusedWindow } = useMethodActions()
  const focusedWindow = useMethodStore(state => state.focusedWindow)

  const setTempDynamicDock = useDockStore(state => state.setTempDynamicDock)
  const tempDynamicDock = useDockStore(state => state.tempDynamicDock)

  const windowControlButtonsOnRight = useSettingsStore(state => state.windowControlButtonsOnRight)

  const isSmallScreen = window.matchMedia("(max-width: 640px)").matches
  const isMediumScreen = window.matchMedia("(max-width: 768px)").matches
  const isLargeScreen = window.matchMedia("(max-width: 1024px)").matches

  return (
    <>
      {trigger}
      <Draggable
        onMouseDown={e => e.stopPropagation()}
        bounds="#root"
        defaultPosition={{ x: isLargeScreen ? (isSmallScreen ? -8 : -16) : 200, y: isLargeScreen ? (isSmallScreen ? -8 : -16) : 50 }}
        onDrag={(_, data) => {
          if (hasFullScreen) {
            if (isOpen === "floating" && data.y <= 0) {
              setMaximizeEffect(true)
            } else if (isOpen === "maximized") {
              if (maximizeEffect) setMaximizeEffect(false);
              setTempDynamicDock(false)
              setIsOpen("floating")
            } else {
              if (maximizeEffect) setMaximizeEffect(false)
            }
          }
        }}
        onStop={(_, data) => {
          if (hasFullScreen) {
            if (isOpen === "floating" && data.y <= 0) {
              setIsOpen("maximized")
              setTempDynamicDock(true)
              setMaximizeEffect(false)
            }
          }
        }}
        handle=".draggable-header"
        disabled={isOpen === "minimized" || (enableDragOn === "md" && isMediumScreen) || (enableDragOn === "lg" && isLargeScreen)}
        position={isOpen === "maximized" ? { x: 0, y: 0 } : undefined}
      >
        <div
          onContextMenu={e => e.preventDefault()}
          onFocus={() => setFocusedWindow(applicationName)}
          onClick={() => setFocusedWindow(applicationName)}
          className={`absolute ${focusedWindow === applicationName ? "z-30" : "z-20"
            } ${(isOpen === "maximized" || isOpen === "floating") ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          style={{
            top: isOpen === "maximized" ? 0 : undefined,
            left: isOpen === "maximized" ? 0 : undefined,
            width: isOpen === "maximized" ? "100vw" : "auto",
            height: isOpen === "maximized" ? "100vh" : "auto",
            borderRadius: isOpen === "maximized" ? "0" : "8px",
            transition: "width 0.3s ease, height 0.3s ease, top 0.3s ease, left 0.3s ease, border-radius 0.3s ease, opacity 0.2s ease",
          }}
        >
          <div
            className={`${isOpen === "floating" || isOpen === "maximized" ? "scale-100" : 'scale-75'} ${isOpen === "maximized" && "h-screen"} transition-all duration-200 ease-in-out`}
          >
            {/* Header (Draggable Handle) */}
            <div
              onClick={() => setFocusedWindow(applicationName)}
              onFocus={() => setFocusedWindow(applicationName)}
              className={"draggable-header select-none flex items-center justify-between cursor-grab active:cursor-grabbing dark:bg-zinc-800 dark:text-zinc-100 text-zinc-800 bg-gray-200 p-2"}
              style={{
                borderRadius: isOpen === "maximized" ? "0" : "8px 8px 0 0",
              }}
            >
              {!windowControlButtonsOnRight ? <div className="space-x-2 ml-2 flex justify-center items-center">
                <button
                  onClick={() => {
                    setIsOpen("closed");
                    if (tempDynamicDock) setTempDynamicDock(false);
                  }
                  }
                  className="bg-red-600 text-red-600 hover:text-zinc-100 rounded-full h-5 w-5 flex items-center justify-center text-sm cursor-pointer">
                  <IoMdClose />
                </button>
                {hasFullScreen && <button
                  onClick={() => {
                    if (isOpen === "maximized") {
                      setIsOpen("floating")
                      if (tempDynamicDock) setTempDynamicDock(false);
                    } else {
                      setIsOpen("maximized")
                      setTempDynamicDock(true)
                    }
                  }
                  }
                  className={`bg-yellow-600 text-yellow-600 hover:text-zinc-100 rounded-full h-5 w-5 ${enableDragOn === "always" && "flex"} ${enableDragOn === "lg" && "hidden lg:flex"} ${enableDragOn === "md" && "hidden md:flex"} items-center justify-center text-sm cursor-pointer`}>
                  {isOpen === "floating" ? <IoMdExpand /> : <IoMdContract />}
                </button>}
                <button onClick={() => {
                  setIsOpen("minimized")
                  if (tempDynamicDock) setTempDynamicDock(false);
                }}
                  className="bg-green-600 text-green-600 hover:text-zinc-100 rounded-full h-5 w-5 flex items-center justify-center text-sm cursor-pointer">
                  <IoMdRemove />
                </button>
              </div> : <span className={hasFullScreen ? "w-[5.25rem]" : "w-14"}></span>}
              <span className="text-base font-semibold">{applicationName}</span>
              {windowControlButtonsOnRight ? <div className="space-x-2 mr-2 flex justify-center items-center">
                <button
                  onClick={() => {
                    setIsOpen("minimized");
                    if (tempDynamicDock) setTempDynamicDock(false);
                  }}
                  className="bg-green-600 text-green-600 hover:text-zinc-100 rounded-full h-5 w-5 flex items-center justify-center text-sm cursor-pointer">
                  <IoMdRemove />
                </button>
                {hasFullScreen && <button
                  onClick={() => {
                    if (isOpen === "maximized") {
                      setIsOpen("floating")
                      if (tempDynamicDock) setTempDynamicDock(false);
                    } else {
                      setIsOpen("maximized")
                      setTempDynamicDock(true)
                    }
                  }
                  }
                  className={`bg-yellow-600 text-yellow-600 hover:text-zinc-100 rounded-full h-5 w-5 ${enableDragOn === "always" && "flex"} ${enableDragOn === "lg" && "hidden lg:flex"} ${enableDragOn === "md" && "hidden md:flex"} items-center justify-center text-sm cursor-pointer`}>
                  {isOpen === "floating" ? <IoMdExpand /> : <IoMdContract />}
                </button>}
                <button
                  onClick={() => {
                    setIsOpen("closed");
                    if (tempDynamicDock) setTempDynamicDock(false);
                  }}
                  className="bg-red-600 text-red-600 hover:text-zinc-100 rounded-full h-5 w-5 flex items-center justify-center text-sm cursor-pointer">
                  <IoMdClose />
                </button>
              </div> : <span className={hasFullScreen ? "w-[5.25rem]" : "w-14"}></span>}
            </div>

            {/* Content */}
            <div className={`border-x-1 border-b-1 border-zinc-200 dark:border-zinc-800 ${enableDragOn !== "always" && "h-[calc(100vh-6.7rem)] w-screen"} ${enableDragOn === "lg" && "lg:h-auto lg:w-fit"} ${enableDragOn === "md" && "md:h-auto md:w-fit"} ${isOpen === "maximized" && "!h-[calc(100vh-2.5rem)] !w-full"}`} onClick={(e) => e.stopPropagation}>
              {children}
            </div>
          </div>
        </div>
      </Draggable>
      <div className={`pointer-events-none bg-blue-400/30 border-2 border-blue-500 fixed z-10 top-0 left-1/2 transform -translate-x-1/2 transition-all duration-200 ease-in-out ${maximizeEffect ? "w-full h-full" : "w-10 h-0 border-none"}`}>
      </div>
    </>
  );
}
